import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import "../styles/travelDetail.css";

export default function TravelDetail() {
  const { id } = useParams();
  const [travel, setTravel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorScreen, setErrorScreen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  useEffect(() => {
    // Zatvori modal na ESC tipku
    const handleEscape = (e) => {
      if (e.key === "Escape" && selectedRestaurant) {
        setSelectedRestaurant(null);
      }
    };

    if (selectedRestaurant) {
      document.addEventListener("keydown", handleEscape);
      // Spriječi scroll pozadine kada je modal otvoren
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [selectedRestaurant]);

  useEffect(() => {
    async function load() {
      try {
        // Try to load with travel_images first
        let { data, error } = await supabase
          .from("travels")
          .select("*, travel_images(*)")
          .eq("id", id)
          .single();

        // If travel_images table doesn't exist, fallback to basic select
        if (error && error.message.includes("Could not find")) {
          const { data: basicData, error: basicError } = await supabase
            .from("travels")
            .select("*")
            .eq("id", id)
            .single();
          
          if (basicError || !basicData) {
            throw basicError || new Error("Travel not found");
          }
          
          data = { ...basicData, travel_images: [] };
          error = null;
        }

        if (error || !data) {
          throw error || new Error("Travel not found");
        }

        // Load restaurant recommendations if table exists
        try {
          const { data: restaurantsData, error: restaurantsError } = await supabase
            .from("travel_restaurants")
            .select("*")
            .eq("travel_id", id)
            .order("created_at", { ascending: true });

          console.log("Restaurants data loaded:", restaurantsData);
          console.log("Restaurants error:", restaurantsError);

          if (restaurantsData && restaurantsData.length > 0) {
            data.restaurants = restaurantsData;
            console.log("Restaurants set:", data.restaurants);
          } else {
            data.restaurants = [];
            console.log("No restaurants found");
          }
        } catch (restErr) {
          // Table doesn't exist, that's okay
          console.log("travel_restaurants table not found or error:", restErr);
          data.restaurants = [];
        }

        setTravel(data);
      } catch (err) {
        console.error("Greška pri učitavanju putovanja:", err);
        setErrorScreen(true);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [id]);

  if (loading)
    return <p className="loading">Učitavanje putovanja…</p>;

  if (errorScreen)
    return (
      <div className="error-message">
        <h2>Ups! Nešto je pošlo po zlu</h2>
        <p>Nije moguće učitati putovanje. Molimo pokušajte ponovo.</p>
        <Link to="/putovanja" className="btn-back">← Vrati se na putovanja</Link>
      </div>
    );

  if (!travel) return null;

  const mainImage = travel.travel_images?.[0]?.image_url || travel.image_url;

  return (
    <div className="travel-detail">
      {mainImage && (
        <div className="travel-hero">
          <img src={mainImage} alt={travel.title} />
        </div>
      )}

      <div className="travel-header">
        <h1>{travel.title}</h1>
      </div>

      {/* Additional images if available */}
      {travel.travel_images && travel.travel_images.length > 1 && (
        <div className="more-images">
          {travel.travel_images.slice(1).map((img) => (
            <img key={img.id} src={img.image_url} alt={travel.title} />
          ))}
        </div>
      )}

      {/* Opis putovanja i preporuke restorana u jednom postu */}
      <div className="travel-body">
        {travel.description && (
          <p className="travel-description">{travel.description}</p>
        )}

        {/* Preporuke restorana ispod opisa */}
        {(() => {
          console.log("Rendering - travel.restaurants:", travel.restaurants);
          console.log("Rendering - restaurants length:", travel.restaurants?.length);
          
          if (travel.restaurants && Array.isArray(travel.restaurants) && travel.restaurants.length > 0) {
            return (
              <div className="travel-restaurants" style={{ marginTop: "40px", paddingTop: "40px", borderTop: "1px solid #e0e0e0" }}>
                <h2 style={{ marginBottom: "24px", fontSize: "1.8rem", fontFamily: "Playfair Display, serif", color: "#1f6074" }}>
                  🍽️ Preporuke restorana ({travel.restaurants.length})
                </h2>
                <div className="restaurants-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "24px" }}>
                  {travel.restaurants.map((restaurant, index) => {
                    console.log(`Rendering restaurant ${index}:`, restaurant);
                    return (
                      <div 
                        key={restaurant.id || index} 
                        className="restaurant-card" 
                        style={{ 
                          border: "1px solid #e0e0e0", 
                          borderRadius: "12px", 
                          overflow: "hidden",
                          background: "#fff",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          cursor: "pointer"
                        }}
                        onClick={() => setSelectedRestaurant(restaurant)}
                      >
                        {restaurant.image_url && (
                          <img
                            src={restaurant.image_url}
                            alt={restaurant.name || restaurant.description || "Restoran"}
                            className="restaurant-image"
                            style={{
                              width: "100%",
                              height: "200px",
                              objectFit: "cover",
                              display: "block"
                            }}
                            onError={(e) => {
                              console.error("Error loading image:", restaurant.image_url);
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <div className="restaurant-info" style={{ padding: "16px" }}>
                          {restaurant.name && (
                            <h3 className="restaurant-name" style={{ margin: "0 0 8px 0", fontSize: "1.2rem", color: "#333", fontWeight: "600" }}>
                              {restaurant.name}
                            </h3>
                          )}
                          {restaurant.description && (
                            <p style={{ margin: "8px 0 0 0", fontSize: "0.95rem", color: "#666", lineHeight: "1.5" }}>
                              {restaurant.description}
                            </p>
                          )}
                          {restaurant.dish && (
                            <p style={{ margin: "8px 0 0 0", fontSize: "0.9rem", color: "#888", fontStyle: "italic" }}>
                              🍴 {restaurant.dish}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          } else {
            console.log("No restaurants to display");
            return null;
          }
        })()}
      </div>

      <div className="travel-actions">
        <Link to="/putovanja" className="btn-back">
          ← Povratak na putovanja
        </Link>
      </div>

      {/* Modal za preporuku restorana */}
      {selectedRestaurant && (
        <div 
          className="restaurant-modal-overlay"
          onClick={() => setSelectedRestaurant(null)}
        >
          <div 
            className="restaurant-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="restaurant-modal-close"
              onClick={() => setSelectedRestaurant(null)}
              aria-label="Zatvori"
            >
              ×
            </button>
            
            {selectedRestaurant.image_url && (
              <div className="restaurant-modal-image">
                <img
                  src={selectedRestaurant.image_url}
                  alt={selectedRestaurant.name || selectedRestaurant.description || "Restoran"}
                />
              </div>
            )}
            
            <div className="restaurant-modal-content">
              {selectedRestaurant.name && (
                <h2 className="restaurant-modal-name">
                  {selectedRestaurant.name}
                </h2>
              )}
              
              {selectedRestaurant.description && (
                <p className="restaurant-modal-description">
                  {selectedRestaurant.description}
                </p>
              )}
              
              {selectedRestaurant.dish && (
                <p className="restaurant-modal-dish">
                  🍴 <strong>Preporučeno jelo:</strong> {selectedRestaurant.dish}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



